import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Plus, Pencil, Trash2, Gauge, TrendingUp, Calendar, BarChart3 } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../contexts/I18nContext";

export default function AutomezziKmPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditAutomezzi = canEdit("automezzi");
  
  const [kmList, setKmList] = useState([]);
  const [statistiche, setStatistiche] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [activeTab, setActiveTab] = useState("storico");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [form, setForm] = useState({
    automezzo_id: "",
    data: new Date().toISOString().split('T')[0],
    km: "",
    note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [kmRes, statsRes, autoRes] = await Promise.all([
        api.get(`${getApiPrefix()}/automezzi/km/lista`),
        api.get(`${getApiPrefix()}/automezzi/km/statistiche`),
        api.get(`${getApiPrefix()}/automezzi`)
      ]);
      setKmList(kmRes.data);
      setStatistiche(statsRes.data);
      setAutomezzi(autoRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      automezzo_id: "",
      data: new Date().toISOString().split('T')[0],
      km: "",
      note: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      automezzo_id: item.automezzo_id || "",
      data: item.data || "",
      km: item.km ? String(item.km) : "",
      note: item.note || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.automezzo_id || !form.data || !form.km) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        data: form.data,
        km: parseInt(form.km),
        note: form.note || null
      };
      
      if (editingId) {
        await api.put(`${getApiPrefix()}/automezzi/km/${editingId}`, payload);
        showSuccess(t("success.kmUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/automezzi/km`, {
          ...payload,
          automezzo_id: form.automezzo_id
        });
        showSuccess(t("success.kmRegistered"));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`${getApiPrefix()}/automezzi/km/${deleteDialog.item.id}`);
      showSuccess(t("success.registrationDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    for (const id of ids) {
      try {
        await api.delete(`${getApiPrefix()}/automezzi/km/${id}`);
        deleted++;
      } catch (err) {}
    }
    showSuccess(`${deleted} registrazioni eliminate`);
    fetchData();
  };

  // Stats globali
  const totaleKmPercorsi = statistiche.reduce((sum, s) => sum + (s.km_percorsi || 0), 0);
  const mediaGiornalieraFlotta = statistiche.length > 0 
    ? (statistiche.reduce((sum, s) => sum + (s.media_giornaliera || 0), 0) / statistiche.length).toFixed(1)
    : 0;

  // Colonne storico KM
  const storicoColumns = [
    {
      key: 'veicolo',
      label: 'Veicolo',
      getValue: (row) => `${row.marca} ${row.modello}`,
      render: (value, row) => (
        <div>
          <span className="font-medium">{value}</span>
          <div className="text-xs text-muted-foreground font-mono">{row.targa}</div>
        </div>
      )
    },
    { key: 'data', label: t('common.date') },
    { 
      key: 'km', 
      label: 'KM',
      align: 'right',
      render: (v) => <span className="font-mono font-medium">{v.toLocaleString('it-IT')}</span>
    },
    { key: 'note', label: t('common.notes'), render: (v) => v || '-' },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  // Colonne statistiche
  const statsColumns = [
    {
      key: 'veicolo',
      label: 'Veicolo',
      getValue: (row) => `${row.marca} ${row.modello}`,
      render: (value, row) => (
        <div>
          <span className="font-medium">{value}</span>
          <div className="text-xs text-muted-foreground font-mono">{row.targa}</div>
        </div>
      )
    },
    { 
      key: 'primo_km', 
      label: 'Primo KM',
      align: 'right',
      render: (v) => v ? v.toLocaleString('it-IT') : '-'
    },
    { 
      key: 'ultimo_km', 
      label: 'Ultimo KM',
      align: 'right',
      render: (v) => v ? <span className="font-mono font-medium">{v.toLocaleString('it-IT')}</span> : '-'
    },
    { 
      key: 'km_percorsi', 
      label: 'KM Percorsi',
      align: 'right',
      render: (v) => <span className="font-mono text-green-600">+{(v || 0).toLocaleString('it-IT')}</span>
    },
    { 
      key: 'giorni_monitorati', 
      label: 'Giorni',
      align: 'right',
      render: (v) => v || '-'
    },
    { 
      key: 'media_giornaliera', 
      label: 'Media/Giorno',
      align: 'right',
      render: (v) => v ? <span className="font-mono">{v}</span> : '-'
    },
    { 
      key: 'media_settimanale', 
      label: 'Media/Sett.',
      align: 'right',
      render: (v) => v ? <span className="font-mono">{v}</span> : '-'
    },
    { 
      key: 'media_mensile', 
      label: 'Media/Mese',
      align: 'right',
      render: (v) => v ? <span className="font-mono font-medium text-blue-600">{v}</span> : '-'
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="km-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("nav.kmHistory")}</h1>
        <p className="text-muted-foreground text-sm">Monitora e analizza i KM percorsi dai veicoli</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Gauge className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Registrazioni</p>
                <p className="text-2xl font-bold tracking-tight">{kmList.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">KM Totali Percorsi</p>
                <p className="text-2xl font-bold font-mono text-green-600">{totaleKmPercorsi.toLocaleString('it-IT')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Media/Giorno Flotta</p>
                <p className="text-2xl font-bold font-mono">{mediaGiornalieraFlotta} km</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Veicoli Monitorati</p>
                <p className="text-2xl font-bold tracking-tight">{statistiche.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="storico">Storico Registrazioni</TabsTrigger>
          <TabsTrigger value="statistiche">Statistiche per Veicolo</TabsTrigger>
        </TabsList>

        <TabsContent value="storico">
          <Card>
            <CardHeader>
              <CardTitle>Registrazioni KM</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={storicoColumns}
                data={kmList}
                onDelete={handleBulkDelete}
                idField="id"
                emptyMessage="Nessuna registrazione KM"
                exportFileName="storico_km"
                toolbar={
                  <Button onClick={openAddDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Registra KM
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistiche">
          <Card>
            <CardHeader>
              <CardTitle>Statistiche KM per Veicolo</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={statsColumns}
                data={statistiche}
                selectable={false}
                idField="automezzo_id"
                emptyMessage="Nessuna statistica disponibile - registra almeno 2 letture KM per ogni veicolo"
                exportFileName="statistiche_km"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t("km.editKm") : t("km.newKm")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('leasing.vehicle')} *</Label>
              <SearchableSelect
                options={automezzi.map(a => ({ value: a.id, label: `${a.marca} ${a.modello} - ${a.targa}${a.ultimo_km ? ` (Ultimo: ${a.ultimo_km.toLocaleString('it-IT')} km)` : ''}` }))}
                value={form.automezzo_id}
                onValueChange={(v) => setForm({ ...form, automezzo_id: v })}
                disabled={!!editingId}
                placeholder={t("dialogs.selectVehicle")}
                searchPlaceholder="Cerca veicolo..."
                emptyMessage="Nessun veicolo trovato"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <DateInput value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>KM Attuali *</Label>
                <Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} placeholder="Es: 125000" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Es: Controllo periodico" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.save"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>Eliminare questa registrazione KM?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })} disabled={deleting}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
